import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { websiteApi, ThemeTokens, ThemePreset } from '../api/website.api';

interface ThemeEditorProps {
  websiteId: string;
  previewUrl: string;
}

const COLOR_FIELDS: { key: keyof ThemeTokens; label: string }[] = [
  { key: 'primaryColor', label: 'Primary' },
  { key: 'secondaryColor', label: 'Secondary' },
  { key: 'accentColor', label: 'Accent' },
  { key: 'backgroundColor', label: 'Page Background' },
  { key: 'cardBackgroundColor', label: 'Card Background' },
  { key: 'textColor', label: 'Body Text' },
  { key: 'mutedTextColor', label: 'Muted Text' },
  { key: 'headingColor', label: 'Headings' },
  { key: 'borderColor', label: 'Borders' },
];

/** Component-level surfaces — auto-derived per section if left blank, but can be pinned to an exact color here. */
const COMPONENT_COLOR_FIELDS: { key: keyof ThemeTokens; label: string; hint: string }[] = [
  { key: 'iconBackgroundColor', label: 'Icon Tile Background', hint: 'Circles behind service/step/social icons' },
  { key: 'iconForegroundColor', label: 'Icon Tile Foreground', hint: 'Glyph color drawn on the icon tile' },
  { key: 'badgeBackgroundColor', label: 'Badge Background', hint: 'Offer discount ribbons, "Popular" plan badges, blog tags' },
  { key: 'badgeTextColor', label: 'Badge Text', hint: 'Text drawn on top of the badge background' },
  { key: 'inputBackgroundColor', label: 'Form Input Background', hint: 'Contact form and newsletter signup fields' },
];

function PresetSwatches({ tokens }: { tokens: ThemeTokens }) {
  const swatches = [tokens.primaryColor, tokens.secondaryColor, tokens.accentColor, tokens.backgroundColor];
  return (
    <div className="flex -space-x-1.5">
      {swatches.map((color, i) => (
        <div
          key={i}
          className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
          style={{ backgroundColor: color || '#e5e7eb' }}
        />
      ))}
    </div>
  );
}

const ThemeEditor: React.FC<ThemeEditorProps> = ({ websiteId, previewUrl }) => {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ThemeTokens | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: theme, isLoading: themeLoading } = useQuery({
    queryKey: ['theme', websiteId],
    queryFn: () => websiteApi.getTheme(websiteId),
    enabled: !!websiteId,
  });

  const { data: presets = [], isLoading: presetsLoading } = useQuery({
    queryKey: ['themePresets'],
    queryFn: () => websiteApi.getThemePresets(),
  });

  useEffect(() => {
    if (theme?.tokens) {
      setDraft(theme.tokens);
    }
  }, [theme]);

  const applyPresetMutation = useMutation({
    mutationFn: (presetId: string) => websiteApi.applyThemePreset(websiteId, presetId),
    onSuccess: (updatedTheme) => {
      queryClient.setQueryData(['theme', websiteId], updatedTheme);
      setDraft(updatedTheme.tokens);
      flashSaved();
    },
  });

  const saveThemeMutation = useMutation({
    mutationFn: (tokens: Partial<ThemeTokens>) => websiteApi.updateTheme(websiteId, tokens),
    onSuccess: (updatedTheme) => {
      queryClient.setQueryData(['theme', websiteId], updatedTheme);
      setDraft(updatedTheme.tokens);
      flashSaved();
    },
  });

  const flashSaved = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateField = (key: keyof ThemeTokens, value: string) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const hasUnsavedChanges = !!draft && !!theme?.tokens && JSON.stringify(draft) !== JSON.stringify(theme.tokens);

  if (themeLoading || !draft) {
    return (
      <div className="nova-card p-4 sm:p-6">
        <p className="text-nova-text3">Loading theme...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="nova-card p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-2">
          <div>
            <h2 className="text-2xl font-bold text-nova-text">Theme Customization</h2>
            <p className="text-sm text-nova-text3 mt-1">
              Start from a curated preset, then fine-tune colors, typography and shape.
              {theme?.presetId && (
                <span className="ml-1">
                  Currently using <strong>{presets.find((p: ThemePreset) => p.id === theme.presetId)?.name || theme.presetId}</strong>.
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
            {saved && <span className="text-sm text-green-600 font-medium text-center sm:text-left">Saved ✓</span>}
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-nova-border rounded-lg text-sm text-nova-text2 hover:bg-nova-bg2 transition-colors text-center sm:w-auto"
            >
              Open Live Preview ↗
            </a>
            <button
              onClick={() => draft && saveThemeMutation.mutate(draft)}
              disabled={!hasUnsavedChanges || saveThemeMutation.isPending}
              className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto justify-center"
            >
              {saveThemeMutation.isPending ? 'Saving...' : 'Save Theme'}
            </button>
          </div>
        </div>
      </div>

      {/* Preset picker */}
      <div className="nova-card p-4 sm:p-6">
        <h3 className="font-semibold text-nova-text mb-4">Theme Presets</h3>
        {presetsLoading ? (
          <p className="text-nova-text3 text-sm">Loading presets...</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {presets.map((preset: ThemePreset) => {
              const isActive = theme?.presetId === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => applyPresetMutation.mutate(preset.id)}
                  disabled={applyPresetMutation.isPending}
                  className={`text-left p-4 rounded-lg border-2 transition-colors ${
                    isActive ? 'border-nova-sky bg-nova-sky/5' : 'border-nova-border hover:border-nova-sky/50'
                  } disabled:opacity-60`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <PresetSwatches tokens={preset.tokens} />
                    {isActive && <span className="text-xs font-semibold text-nova-sky">ACTIVE</span>}
                  </div>
                  <p className="font-semibold text-nova-text text-sm">{preset.name}</p>
                  <p className="text-xs text-nova-text3 mt-1 leading-snug">{preset.description}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Colors */}
        <div className="nova-card p-4 sm:p-6">
          <h3 className="font-semibold text-nova-text mb-4">Colors</h3>
          <div className="space-y-4">
            {COLOR_FIELDS.map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-nova-text2 mb-2">{label}</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={(draft[key] as string) || '#ffffff'}
                    onChange={(e) => updateField(key, e.target.value)}
                    className="h-10 w-14 border border-nova-border rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={(draft[key] as string) || ''}
                    onChange={(e) => updateField(key, e.target.value)}
                    placeholder="#000000"
                    className="flex-1 px-3 py-2 border border-nova-border rounded-lg font-mono text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          {/* Typography & shape */}
          <div className="nova-card p-4 sm:p-6">
            <h3 className="font-semibold text-nova-text mb-4">Typography & Shape</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-nova-text2 mb-2">Body Font</label>
                <input
                  type="text"
                  value={draft.fontFamily || ''}
                  onChange={(e) => updateField('fontFamily', e.target.value)}
                  placeholder='"Inter", sans-serif'
                  className="w-full px-3 py-2 border border-nova-border rounded-lg font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nova-text2 mb-2">Heading Font</label>
                <input
                  type="text"
                  value={draft.headingFontFamily || ''}
                  onChange={(e) => updateField('headingFontFamily', e.target.value)}
                  placeholder='"Poppins", sans-serif'
                  className="w-full px-3 py-2 border border-nova-border rounded-lg font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nova-text2 mb-2">Corner Roundness</label>
                <input
                  type="text"
                  value={draft.borderRadius || ''}
                  onChange={(e) => updateField('borderRadius', e.target.value)}
                  placeholder="10px"
                  className="w-full px-3 py-2 border border-nova-border rounded-lg font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Live mini-preview reflecting unsaved edits */}
          <div className="nova-card p-4 sm:p-6">
            <h3 className="font-semibold text-nova-text mb-4">Live Preview</h3>
            <div
              className="rounded-lg overflow-hidden border border-nova-border"
              style={{
                backgroundColor: draft.backgroundColor,
                fontFamily: draft.fontFamily,
                padding: '1.5rem',
              }}
            >
              <h4
                style={{
                  color: draft.headingColor || draft.textColor,
                  fontFamily: draft.headingFontFamily || draft.fontFamily,
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '0.5rem',
                }}
              >
                Your Business Name
              </h4>
              <p style={{ color: draft.textColor, fontSize: '0.9rem', marginBottom: '1rem' }}>
                This is a live preview of your body text and colors as you edit them.
              </p>
              <div
                style={{
                  backgroundColor: draft.cardBackgroundColor || draft.backgroundColor,
                  border: `1px solid ${draft.borderColor || '#e5e7eb'}`,
                  borderRadius: draft.borderRadius,
                  boxShadow: draft.design?.cardShadow || draft.shadow,
                  padding: '1rem',
                  marginBottom: '1rem',
                }}
              >
                <p style={{ color: draft.headingColor || draft.textColor, fontWeight: 600, marginBottom: '0.25rem' }}>
                  Sample Card
                </p>
                <p style={{ color: draft.mutedTextColor || draft.textColor, fontSize: '0.85rem' }}>
                  Muted supporting text for captions and metadata.
                </p>
              </div>
              <button
                style={{
                  background: draft.design?.buttonGradient && draft.design.buttonGradient !== 'none'
                    ? draft.design.buttonGradient
                    : draft.primaryColor,
                  color: '#FFFFFF',
                  borderRadius: draft.borderRadius,
                  padding: '0.6rem 1.25rem',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                }}
              >
                Call to Action
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Component-level surfaces */}
      <div className="nova-card p-4 sm:p-6">
        <h3 className="font-semibold text-nova-text mb-1">Component Colors</h3>
        <p className="text-xs text-nova-text3 mb-4">
          Icon tiles, badges/ribbons, and form inputs are auto-derived to match each section&apos;s background — leave
          these blank unless you want to pin an exact color.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          {COMPONENT_COLOR_FIELDS.map(({ key, label, hint }) => (
            <div key={key}>
              <label className="block text-sm font-medium text-nova-text2 mb-1">{label}</label>
              <p className="text-xs text-nova-text3 mb-2">{hint}</p>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={(draft[key] as string) || '#ffffff'}
                  onChange={(e) => updateField(key, e.target.value)}
                  className="h-10 w-14 border border-nova-border rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={(draft[key] as string) || ''}
                  onChange={(e) => updateField(key, e.target.value)}
                  placeholder="Auto"
                  className="flex-1 px-3 py-2 border border-nova-border rounded-lg font-mono text-sm"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 bg-nova-sky/10 rounded-lg">
        <p className="text-sm text-nova-sky">
          <strong>Tip:</strong> Applying a preset replaces all colors, fonts and effects at once. Editing a field
          below fine-tunes the current theme without losing the rest of your customizations.
        </p>
      </div>
    </div>
  );
};

export default ThemeEditor;
